/**
 * Repository of Job Order constants and Helpers
 */

export const JOB_TYPES = [
  "AG Installation",
  "Ant Control",
  "Backjob",
  "Basic Rodent Control",
  "Billing",
  "Cockroach Control",
  "Cockroach Gel Bait/Powder",
  "Collection",
  "Comprehensive Termite",
  "Daily Monitoring",
  "Deep Cleaning",
  "Deliver Certificate",
  "Deliver Service Invoice",
  "Delivery of Materials",
  "Disinfection",
  "Exterra Inground",
  "Facility Audit",
  "Fogging",
  "GPC",
  "Heat Treatment",
  "ILT Monitoring",
  "In2Care Installation",
  "In2Care Monitoring",
  "Intensive Rodent Control",
  "Logistics",
  "Meeting/Seminar",
  "Monitoring",
  "Mosquito Control",
  "Ocular",
  "Phase 1 and Phase 2",
  "Pheromone Trap Installation",
  "Pheromone Trap Monitoring",
  "QA Visit",
  "QPMS",
  "QTM",
  "QuickCert",
  "Rodent Monitoring",
  "Termipore Installation",
  "Termite Baiting",
  "Termite Control",
  "Termite Inspection",
  "Termite Spot Spraying",
  "Vacuuming"
];

export const JOB_DESCRIPTIONS = [
  "AG Installation",
  "AG Monitoring",
  "AG Pullout",
  "Cold Fogging",
  "Comprehensive Termite Control",
  "Disinfection",
  "Fogging",
  "GPC with Blower and Vacuum",
  "In2Care Monitoring",
  "Logistics (Billing, Collection, Delivery)",
  "Mistblower",
  "Misting",
  "Pheromone Trap Monitoring",
  "Powder/Gel Baiting",
  "QA Visit/Facility Audit",
  "Rodent Monitoring",
  "Rodent Trapping",
  "Soil Poisoning",
  "Spraying",
  "Termatrac Monitoring",
  "Termipore, In2Care, Pheromone Trap, or Device Installation",
  "UV Light",
  "Wiping"
];

export const DOCUMENTS_TO_BRING = [
  "Acknowledgement Form",
  "Audit Docs",
  "Billing Statement",
  "Certificate",
  "Chemical SDS, CPR",
  "Collection Receipt",
  "Company Cellphone",
  "Contract",
  "Delivery Receipt",
  "Facility Audit Form",
  "Facility Map",
  "Monitoring Forms",
  "Service Invoice",
  "Service Report",
  "SOA",
  "TSDR",
  "Traceability Form",
  "Work Permit / Gate Pass"
];

export const EQUIPMENT = [
  "Cartons for Bosny",
  "Cold Fogger",
  "Distilled Water",
  "Flashlight",
  "Fogging Machine",
  "GBS",
  "Gel Bait",
  "Handsprayer",
  "ILT",
  "Injector",
  "Knapsack Sprayer",
  "Label",
  "Makita Drill",
  "Masking/Duct Tape",
  "Mist Blower",
  "Misting",
  "No more nails",
  "Powder",
  "Power Sprayer and Hose",
  "RBS",
  "Rags",
  "Rat Cage",
  "Screwdriver",
  "Sprayer",
  "Steamer",
  "Termatrac",
  "UV Light"
];

// --- token-level casing exceptions (minimal) ---
const SPECIAL_TOKEN_CASE = {
  ag: "AG",
  qa: "QA",
  ilt: "ILT",
  gpc: "GPC",
  uv: "UV",
  qtm: "QTM",
  qpms: "QPMS",
  in2care: "In2Care",
  // docs/equipment acronyms
  sds: "SDS",
  cpr: "CPR",
  soa: "SOA",
  tsdr: "TSDR",
  gbs: "GBS",
  rbs: "RBS",
};

// Whole-phrase overrides (rare; use sparingly)
const PHRASE_OVERRIDES = {
  "phase_1_and_phase_2": "Phase 1 and Phase 2",
  "cockroach_gel_bait/powder": "Cockroach Gel Bait/Powder",
};

// Convert snake/slash case keys to nicely cased labels (punctuation-aware)
export const labelFromKey = (key = "") => {
  if (!key) return "";
  if (PHRASE_OVERRIDES[key]) return PHRASE_OVERRIDES[key];

  return key
    .split("/") // keep slash-separated parts as slashes
    .map((segment) =>
      segment
        .split("_")
        .map((tok) => {
          if (!tok) return "";
          // preserve leading/trailing punctuation while casing the core token
          const m = tok.match(/^([^A-Za-z0-9]*)([A-Za-z0-9]+)([^A-Za-z0-9]*)$/);
          if (!m) {
            // no alphanum core; fall back to simple title-case
            return tok.charAt(0).toUpperCase() + tok.slice(1);
          }
          const [, pre, core, post] = m;
          const coreLower = core.toLowerCase();
          const mapped =
            SPECIAL_TOKEN_CASE[coreLower] ||
            (core ? core.charAt(0).toUpperCase() + core.slice(1) : "");
          return `${pre}${mapped}${post}`;
        })
        .join(" ")
    )
    .join("/");
};

// Convert a curated label to a canonical key we store in DB
export const keyFromLabel = (label = "") => {
  const raw = (label || "").trim();

  // Invert phrase overrides for exact label matches
  const invPhrase = Object.entries(PHRASE_OVERRIDES).reduce((acc, [k, v]) => {
    acc[v.toLowerCase()] = k;
    return acc;
  }, {});
  const asPhrase = invPhrase[raw.toLowerCase()];
  if (asPhrase) return asPhrase;

  // Preserve slashes as separators; normalize each side to snake_case
  return raw
    .split("/")
    .map((side) =>
      side
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "_")
        .replace(/_+/g, "_")
        .toLowerCase()
        .replace(/^_+|_+$/g, "")
    )
    .join("/");
};

// Build a key->label lookup from a curated label list
const buildLookup = (labels = []) =>
  Object.fromEntries(labels.map((label) => [keyFromLabel(label), label]));

// --- Lookups & normalizers for each category ---
// (Assumes you already export JOB_TYPES, JOB_DESCRIPTIONS, DOCUMENTS_TO_BRING, EQUIPMENT above)

export const JOB_TYPE_LABEL_BY_KEY = buildLookup(JOB_TYPES);
export const JOB_DESCRIPTION_LABEL_BY_KEY = buildLookup(JOB_DESCRIPTIONS);
export const DOCUMENT_LABEL_BY_KEY = buildLookup(DOCUMENTS_TO_BRING);
export const EQUIPMENT_LABEL_BY_KEY = buildLookup(EQUIPMENT);

export const normalizeJobTypeKeyToLabel = (key) =>
  JOB_TYPE_LABEL_BY_KEY[key] || labelFromKey(key);

export const normalizeJobDescriptionKeyToLabel = (key) =>
  JOB_DESCRIPTION_LABEL_BY_KEY[key] || labelFromKey(key);

export const normalizeDocumentKeyToLabel = (key) =>
  DOCUMENT_LABEL_BY_KEY[key] || labelFromKey(key);

export const normalizeEquipmentKeyToLabel = (key) =>
  EQUIPMENT_LABEL_BY_KEY[key] || labelFromKey(key);